import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import {
  Description as RequestIcon,
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Mail as MailIcon,
  Share as ShareIcon,
  Business as OrgIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const DRAWER_WIDTH = 248;

const ROLE_LABELS = { owner: 'Owner', admin: 'Org Admin', member: 'Member' };
const roleLabel = (role) => ROLE_LABELS[role] || 'Member';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    organizations,
    logout,
    isAdmin,
    isOrgOwner,
    activeOrg,
    activeOrgId,
    activeOrgRole,
    setActiveOrgId,
  } = useAuth();
  const [orgMenuAnchor, setOrgMenuAnchor] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const switchWorkspace = (orgId) => {
    setOrgMenuAnchor(null);
    setActiveOrgId(orgId);
    navigate('/');
  };

  const inAnyOrg = (organizations || []).length > 0;

  // Primary nav
  const primaryMenu = [
    { text: 'Requests', icon: <RequestIcon fontSize="small" />, path: '/requests' },
    { text: 'New Request', icon: <AddIcon fontSize="small" />, path: '/requests/new' },
  ];
  if (inAnyOrg) {
    primaryMenu.push({ text: 'Shared with me', icon: <ShareIcon fontSize="small" />, path: '/shared' });
  }
  primaryMenu.push({ text: 'Sender Inbox', icon: <MailIcon fontSize="small" />, path: '/inbox' });

  // Secondary nav (below divider)
  const secondaryMenu = [
    { text: 'Profile', icon: <PersonIcon fontSize="small" />, path: '/profile' },
  ];
  if (isOrgOwner()) {
    secondaryMenu.push({ text: 'Organization', icon: <OrgIcon fontSize="small" />, path: '/organization' });
  }
  if (isAdmin()) {
    secondaryMenu.push({ text: 'Admin Panel', icon: <AdminIcon fontSize="small" />, path: '/admin' });
  }

  const isActive = (path) => {
    if (path === '/requests') {
      return location.pathname === '/' || location.pathname === '/requests' || (location.pathname.startsWith('/requests/') && !location.pathname.startsWith('/requests/new'));
    }
    if (path === '/requests/new') return location.pathname === '/requests/new';
    return location.pathname.startsWith(path);
  };

  const renderMenuItem = (item) => {
    const active = isActive(item.path);
    return (
      <ListItem
        button
        key={item.text}
        onClick={() => navigate(item.path)}
        sx={{
          mx: 1,
          mb: 0.5,
          borderRadius: 1.5,
          width: 'calc(100% - 16px)',
          backgroundColor: active ? 'rgba(42,82,190,0.08)' : 'transparent',
          borderLeft: active ? '3px solid #2a52be' : '3px solid transparent',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: active ? 'rgba(42,82,190,0.12)' : 'rgba(0,7,94,0.04)',
          },
        }}
      >
        <ListItemIcon sx={{ color: active ? '#2a52be' : '#838996', minWidth: 36 }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.text}
          primaryTypographyProps={{
            sx: {
              fontFamily: '"Helvetica Neue", Arial, sans-serif',
              fontWeight: active ? 600 : 500,
              fontSize: '0.875rem',
              color: active ? '#2a52be' : '#00075e',
            },
          }}
        />
      </ListItem>
    );
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo area */}
      <Box sx={{ px: 2.5, py: 2.5, borderBottom: '1px solid #e0ddd6' }}>
        <Typography
          variant="caption"
          sx={{
            fontFamily: '"Helvetica Neue", Arial, sans-serif',
            fontWeight: 700,
            fontSize: '0.65rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#838996',
            display: 'block',
            mb: 0.5,
          }}
        >
          Berkeley Technology & Justice Lab
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Georgia, serif',
            fontWeight: 700,
            color: '#00075e',
            fontSize: '1.15rem',
            lineHeight: 1.2,
          }}
        >
          PRA Machine
        </Typography>

        {/* Workspace switcher (Slack-style) */}
        {inAnyOrg && (
          <>
            <Button
              size="small"
              onClick={(e) => setOrgMenuAnchor(e.currentTarget)}
              sx={{
                mt: 1,
                width: '100%',
                justifyContent: 'flex-start',
                textTransform: 'none',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: 'rgba(42,82,190,0.06)',
                border: '1px solid rgba(42,82,190,0.15)',
                '&:hover': { backgroundColor: 'rgba(42,82,190,0.1)' },
              }}
            >
              <Box sx={{ flexGrow: 1, textAlign: 'left', minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: '"Helvetica Neue", Arial, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    color: '#00075e',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {activeOrg ? activeOrg.name : 'Select a workspace'}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Helvetica Neue", Arial, sans-serif',
                    fontSize: '0.65rem',
                    color: '#838996',
                    lineHeight: 1.2,
                  }}
                >
                  {activeOrgRole ? roleLabel(activeOrgRole) : `${organizations.length} workspaces`}
                </Typography>
              </Box>
              <ExpandMoreIcon sx={{ color: '#838996', fontSize: 18, ml: 0.5 }} />
            </Button>
            <Menu
              anchorEl={orgMenuAnchor}
              open={Boolean(orgMenuAnchor)}
              onClose={() => setOrgMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { width: 224, mt: 0.5, borderRadius: 1.5, border: '1px solid #e0ddd6' } }}
            >
              <Typography sx={{ px: 2, py: 0.75, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#838996' }}>
                Workspaces
              </Typography>
              {organizations.map((org) => {
                const isActive = activeOrgId === org.id;
                return (
                  <MenuItem
                    key={org.id}
                    onClick={() => switchWorkspace(org.id)}
                    sx={{ py: 1, fontSize: '0.85rem', color: '#00075e' }}
                  >
                    <ListItemIcon sx={{ minWidth: 30, color: isActive ? '#2a52be' : 'transparent' }}>
                      {isActive && <CheckIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={org.name}
                      primaryTypographyProps={{ sx: { fontSize: '0.82rem', fontWeight: isActive ? 600 : 500 } }}
                    />
                    <Typography sx={{ fontSize: '0.65rem', color: '#838996' }}>{roleLabel(org.my_role)}</Typography>
                  </MenuItem>
                );
              })}
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={() => {
                  setOrgMenuAnchor(null);
                  navigate('/org-picker');
                }}
                sx={{ py: 1, fontSize: '0.82rem', color: '#2a52be' }}
              >
                Switch workspace…
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      {/* Nav items */}
      <Box sx={{ flexGrow: 1, py: 1.5 }}>
        <List dense disablePadding>
          {primaryMenu.map(renderMenuItem)}
        </List>
        <Divider sx={{ borderColor: '#e0ddd6', my: 1.5, mx: 2 }} />
        <List dense disablePadding>
          {secondaryMenu.map(renderMenuItem)}
        </List>
      </Box>

      <Divider sx={{ borderColor: '#e0ddd6' }} />

      {/* User info + logout */}
      <Box sx={{ px: 2, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: '#2a52be',
              fontSize: '0.8rem',
              fontFamily: '"Helvetica Neue", Arial, sans-serif',
              fontWeight: 600,
            }}
          >
            {user?.username?.[0]?.toUpperCase() || 'U'}
          </Avatar>
          <Box>
            <Typography
              sx={{
                fontFamily: '"Helvetica Neue", Arial, sans-serif',
                fontWeight: 600,
                fontSize: '0.8rem',
                color: '#00075e',
                lineHeight: 1.2,
              }}
            >
              {user?.full_name || user?.username}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Helvetica Neue", Arial, sans-serif',
                fontSize: '0.7rem',
                color: '#838996',
                lineHeight: 1.2,
              }}
            >
              {user?.role === 'admin' ? 'Administrator' : 'Researcher'}
            </Typography>
          </Box>
        </Box>
        <ListItem
          button
          onClick={handleLogout}
          sx={{
            borderRadius: 1.5,
            px: 1,
            py: 0.75,
            '&:hover': { backgroundColor: 'rgba(0,7,94,0.04)' },
          }}
        >
          <ListItemIcon sx={{ color: '#838996', minWidth: 32 }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Sign Out"
            primaryTypographyProps={{
              sx: {
                fontFamily: '"Helvetica Neue", Arial, sans-serif',
                fontWeight: 500,
                fontSize: '0.8rem',
                color: '#838996',
              },
            }}
          />
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f4f2ec' }}>
      {/* Top banner */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#00075e',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          height: 44,
          justifyContent: 'center',
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 44, px: { xs: 2, sm: 3 } }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'Georgia, serif',
              fontWeight: 700,
              fontSize: '0.85rem',
              letterSpacing: '0.01em',
              color: 'rgba(255,255,255,0.9)',
              flexGrow: 1,
            }}
          >
            Berkeley Technology &amp; Justice Lab
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: '"Helvetica Neue", Arial, sans-serif',
              fontSize: '0.7rem',
              color: 'rgba(255,255,255,0.5)',
              letterSpacing: '0.05em',
            }}
          >
            PRA Machine
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              top: 44,
              height: 'calc(100% - 44px)',
              borderRight: '1px solid #e0ddd6',
              backgroundColor: '#ffffff',
              boxShadow: '2px 0 8px rgba(0,7,94,0.04)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          mt: '44px',
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: 'calc(100vh - 44px)',
          backgroundColor: '#f4f2ec',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
